// ============================================================
// MyAttendancePage — 員工自查出勤紀錄
// ------------------------------------------------------------
// 預設顯示本月，可改日期區間。每筆顯示：
//   日期 / 週幾 / 排班時段 / 上下班打卡時間 / 狀態
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { formatTaipeiTime } from '../lib/datetime';
import { ErrorBanner } from './ErrorBanner';

interface AttendanceRecord {
  id: string;
  clockIn: string | null;
  clockOut: string | null;
  clockInMethod: string | null;
  clockOutMethod: string | null;
  status: string;          // NORMAL | LATE | EARLY_LEAVE | MISSED | PENDING
  approvalStatus: string;  // AUTO_APPROVED | PENDING | APPROVED | REJECTED
  note: string | null;
  // 加班核准
  overtimeMinutes?: number;
  overtimeApprovalStatus?: string;  // NOT_APPLICABLE | PENDING | APPROVED | REJECTED
  overtimeRejectReason?: string | null;
  schedule: {
    date: string;
    startTime: string;
    endTime: string;
    position: string | null;
  } | null;
}

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

/** 打卡方式的顯示名稱 */
const METHOD_LABEL: Record<string, string> = {
  GPS: '定位', QR: 'QR', WIFI: 'WiFi', KIOSK: '打卡站', CORRECTION: '補打卡',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// 顯示以台北時間為準（見 lib/datetime.ts）
function fmtTime(iso: string | null): string {
  return iso ? formatTaipeiTime(iso) : '—';
}

const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  NORMAL: { label: '正常', className: 'bg-sage-100 text-sage-600' },
  LATE: { label: '遲到', className: 'bg-amber-100 text-amber-600' },
  EARLY_LEAVE: { label: '早退', className: 'bg-amber-100 text-amber-600' },
  MISSED: { label: '未打卡', className: 'bg-rose-100 text-rose-600' },
  PENDING: { label: '待處理', className: 'bg-morandi-100 text-morandi-500' },
};

export function MyAttendancePage() {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState<string>(toDateStr(monthStart));
  const [endDate, setEndDate] = useState<string>(toDateStr(today));
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ records: AttendanceRecord[] }>(
        `/attendance/my?startDate=${startDate}&endDate=${endDate}`
      );
      setRecords(r.records);
      setError(null);
    } catch (err) { setError((err as Error).message); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // 統計
  const totalDays = records.filter(r => r.clockIn).length;
  const lateCount = records.filter(r => r.status === 'LATE').length;
  const missedCount = records.filter(r => r.status === 'MISSED').length;
  const totalHours = records.reduce((sum, r) => {
    if (!r.clockIn || !r.clockOut) return sum;
    const ms = new Date(r.clockOut).getTime() - new Date(r.clockIn).getTime();
    return sum + ms / 3600000;
  }, 0);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5 pb-24">
      {error && <ErrorBanner message={error} onRetry={fetchRecords} />}

      <h1 className="text-xl font-bold text-morandi-800">出勤紀錄</h1>

      {/* 日期區間 */}
      <div className="card-morandi p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-morandi-400 block mb-1">開始日期</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="text-xs text-morandi-400 block mb-1">結束日期</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap text-xs">
          <button
            onClick={() => {
              const s = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(toDateStr(s));
              setEndDate(toDateStr(today));
            }}
            className="px-3 py-1.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn"
          >本月</button>
          <button
            onClick={() => {
              const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const e = new Date(today.getFullYear(), today.getMonth(), 0);
              setStartDate(toDateStr(s));
              setEndDate(toDateStr(e));
            }}
            className="px-3 py-1.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn"
          >上月</button>
          <button
            onClick={() => {
              const s = new Date(today);
              s.setDate(s.getDate() - 6);
              setStartDate(toDateStr(s));
              setEndDate(toDateStr(today));
            }}
            className="px-3 py-1.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn"
          >近 7 天</button>
        </div>
      </div>

      {/* 統計 */}
      <div className="grid grid-cols-4 gap-2">
        <div className="card-morandi p-3 text-center">
          <div className="text-xs text-morandi-400">出勤天</div>
          <div className="text-lg font-bold font-mono text-morandi-700">{totalDays}</div>
        </div>
        <div className="card-morandi p-3 text-center">
          <div className="text-xs text-morandi-400">總工時</div>
          <div className="text-lg font-bold font-mono text-morandi-700">{Math.round(totalHours * 10) / 10}</div>
        </div>
        <div className="card-morandi p-3 text-center">
          <div className="text-xs text-morandi-400">遲到</div>
          <div className={`text-lg font-bold font-mono ${lateCount > 0 ? 'text-amber-500' : 'text-morandi-700'}`}>{lateCount}</div>
        </div>
        <div className="card-morandi p-3 text-center">
          <div className="text-xs text-morandi-400">未打卡</div>
          <div className={`text-lg font-bold font-mono ${missedCount > 0 ? 'text-rose-500' : 'text-morandi-700'}`}>{missedCount}</div>
        </div>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : records.length === 0 ? (
        <div className="card-morandi p-8 text-center text-morandi-300">
          所選日期區間沒有出勤紀錄
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => {
            const refDate = r.schedule?.date ?? r.clockIn ?? r.clockOut;
            const date = refDate ? new Date(refDate) : null;
            const status = STATUS_STYLE[r.status] ?? { label: r.status, className: 'bg-morandi-100 text-morandi-500' };
            const workHours = (r.clockIn && r.clockOut)
              ? Math.round(((new Date(r.clockOut).getTime() - new Date(r.clockIn).getTime()) / 3600000) * 10) / 10
              : null;

            return (
              <div key={r.id} className="card-morandi p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium text-morandi-700">
                      {date ? `${date.getMonth() + 1}/${date.getDate()}` : '—'}
                    </span>
                    {date && (
                      <span className="ml-2 text-xs text-morandi-400">週{WEEKDAY[date.getDay()]}</span>
                    )}
                    {r.schedule?.position && (
                      <span className="ml-2 text-xs text-morandi-400">@ {r.schedule.position}</span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${status.className}`}>
                    {status.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-morandi-400">排班</div>
                    <div className="font-mono text-morandi-600">
                      {r.schedule ? `${r.schedule.startTime}–${r.schedule.endTime}` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-morandi-400">實際打卡</div>
                    <div className="font-mono text-morandi-700">
                      {fmtTime(r.clockIn)} – {fmtTime(r.clockOut)}
                    </div>
                  </div>
                </div>

                {workHours != null && (
                  <div className="text-xs text-morandi-500">
                    工時 <span className="font-mono">{workHours}</span> 小時
                    {r.clockInMethod && (
                      <span className="ml-2 text-morandi-300">· {METHOD_LABEL[r.clockInMethod] ?? r.clockInMethod}</span>
                    )}
                  </div>
                )}

                {r.note && (
                  <div className="text-xs text-morandi-500 bg-morandi-50 rounded px-2 py-1">
                    備註：{r.note}
                  </div>
                )}

                {r.approvalStatus === 'PENDING' && (
                  <div className="text-xs text-amber-600">⏳ 等待主管簽核</div>
                )}
                {r.approvalStatus === 'REJECTED' && (
                  <div className="text-xs text-rose-500">✕ 已被駁回</div>
                )}

                {/* 加班核准狀態 */}
                {r.overtimeApprovalStatus === 'PENDING' && (r.overtimeMinutes ?? 0) > 0 && (
                  <div className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                    ⏳ 加班 {r.overtimeMinutes} 分鐘待主管核准
                  </div>
                )}
                {r.overtimeApprovalStatus === 'APPROVED' && (r.overtimeMinutes ?? 0) > 0 && (
                  <div className="text-xs text-sage-600 bg-sage-50 rounded px-2 py-1">
                    ✓ 加班 {r.overtimeMinutes} 分鐘已核准
                  </div>
                )}
                {r.overtimeApprovalStatus === 'REJECTED' && (r.overtimeMinutes ?? 0) > 0 && (
                  <div className="text-xs text-rose-500 bg-rose-50 rounded px-2 py-1">
                    ✕ 加班 {r.overtimeMinutes} 分鐘已被拒絕（不計工時）
                    {r.overtimeRejectReason && <span className="block mt-0.5 text-rose-400">理由：{r.overtimeRejectReason}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
